import React from "react";
import { FaCheckCircle } from "react-icons/fa";

const styles = {
  page: {
    background: "linear-gradient(135deg, #fafafa 0%, #f0f0f8 100%)",
    color: "#1a1a2e",
    minHeight: "100vh",
    padding: "64px 48px",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', sans-serif",
  },

  container: {
    maxWidth: "960px",
    margin: "0 auto",
  },

  // ── Hero ──────────────────────────────────────────────────────────────────
  hero: {
    display: "flex",
    alignItems: "center",
    gap: "28px",
    marginBottom: "52px",
    paddingBottom: "44px",
    borderBottom: "1px solid rgba(139,92,246,0.15)",
  },

  avatar: {
    width: "115px",
    height: "115px",
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid rgba(139,92,246,0.4)",
    boxShadow: "0 8px 32px rgba(139,92,246,0.2)",
    flexShrink: 0,
  },

  heroText: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },

  nameRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  name: {
    fontSize: "34px",
    fontWeight: "800",
    letterSpacing: "-0.03em",
    margin: 0,
    background: "linear-gradient(135deg, #1a1a2e, #7c3aed)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  verifiedIcon: {
    color: "#7c3aed",
    fontSize: "22px",
    marginTop: "2px",
    flexShrink: 0,
  },

  title: {
    fontSize: "16px",
    fontWeight: "500",
    color: "#6b7280",
    margin: 0,
    letterSpacing: "-0.01em",
  },

  subtitle: {
    fontSize: "14px",
    fontWeight: "400",
    color: "#9ca3af",
    margin: 0,
    letterSpacing: "-0.01em",
  },

  // ── Badge ─────────────────────────────────────────────────────────────────
  badge: {
    display: "inline-block",
    background: "linear-gradient(135deg, rgba(124,58,237,0.1), rgba(139,92,246,0.15))",
    border: "1px solid rgba(124,58,237,0.3)",
    color: "#7c3aed",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    padding: "6px 16px",
    borderRadius: "100px",
    marginBottom: "20px",
  },

  // ── Section ───────────────────────────────────────────────────────────────
  section: {
    marginBottom: "52px",
    paddingBottom: "44px",
    borderBottom: "1px solid rgba(139,92,246,0.12)",
  },

  sectionTitle: {
    fontSize: "24px",
    fontWeight: "700",
    letterSpacing: "-0.03em",
    color: "#1a1a2e",
    marginBottom: "16px",
  },

  bodyText: {
    fontSize: "17px",
    lineHeight: "1.8",
    color: "#4b5563",
    letterSpacing: "-0.01em",
  },

  // ── Find Me ───────────────────────────────────────────────────────────────
  findMeGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gap: "14px",
    marginTop: "20px",
  },

  findMeCard: {
    background: "#fff",
    border: "1px solid rgba(139,92,246,0.15)",
    borderRadius: "20px",
    padding: "24px 16px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "12px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(139,92,246,0.08)",
    transition: "box-shadow 0.2s",
  },

  findMeLogo: {
    width: "90px",
    height: "90px",
    objectFit: "contain",
  },

  findMeDesc: {
    fontSize: "13px",
    color: "#9ca3af",
    lineHeight: "1.5",
    letterSpacing: "-0.01em",
  },

  findMeBtn: (bg) => ({
    display: "inline-block",
    padding: "9px 18px",
    backgroundColor: bg,
    color: "#fff",
    borderRadius: "10px",
    textDecoration: "none",
    fontWeight: "600",
    fontSize: "13px",
    letterSpacing: "-0.01em",
    marginTop: "auto",
    boxShadow: `0 4px 12px ${bg}55`,
    transition: "opacity 0.2s",
  }),

  // ── Stats ─────────────────────────────────────────────────────────────────
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: "14px",
    marginTop: "20px",
  },

  statCard: {
    background: "#fff",
    border: "1px solid rgba(139,92,246,0.15)",
    borderRadius: "20px",
    padding: "28px 16px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(139,92,246,0.08)",
  },

  statNumber: {
    fontSize: "36px",
    fontWeight: "800",
    letterSpacing: "-0.04em",
    background: "linear-gradient(135deg, #7c3aed, #a855f7)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },

  statLabel: {
    fontSize: "14px",
    color: "#9ca3af",
    marginTop: "6px",
    letterSpacing: "-0.01em",
    fontWeight: "500",
  },

  // ── Skills ────────────────────────────────────────────────────────────────
  skillsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginTop: "16px",
  },

  skill: {
    background: "#fff",
    border: "1px solid rgba(124,58,237,0.2)",
    color: "#7c3aed",
    padding: "8px 16px",
    borderRadius: "10px",
    fontSize: "14px",
    fontWeight: "500",
    letterSpacing: "-0.01em",
    boxShadow: "0 2px 8px rgba(124,58,237,0.08)",
  },

  // ── Experience ────────────────────────────────────────────────────────────
  expCard: {
    background: "#fff",
    border: "1px solid rgba(139,92,246,0.15)",
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "12px",
    boxShadow: "0 4px 20px rgba(139,92,246,0.07)",
  },

  expRole: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1a1a2e",
    letterSpacing: "-0.02em",
    margin: "0 0 4px",
  },

  expDate: {
    fontSize: "13px",
    color: "#7c3aed",
    fontWeight: "600",
    marginBottom: "8px",
  },

  expDesc: {
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: "1.7",
    letterSpacing: "-0.01em",
    margin: 0,
  },

  // ── Reflex card ───────────────────────────────────────────────────────────
  reflexCard: {
    background: "#fff",
    border: "1px solid rgba(139,92,246,0.15)",
    borderRadius: "16px",
    padding: "24px",
    marginBottom: "12px",
    boxShadow: "0 4px 20px rgba(139,92,246,0.07)",
  },

  reflexRole: {
    fontSize: "16px",
    fontWeight: "700",
    color: "#1a1a2e",
    letterSpacing: "-0.02em",
    margin: "0 0 4px",
  },

  reflexDate: {
    fontSize: "13px",
    color: "#7c3aed",
    fontWeight: "600",
    marginBottom: "16px",
  },

  reflexSubHeading: {
    fontSize: "14px",
    fontWeight: "700",
    color: "#7c3aed",
    letterSpacing: "-0.01em",
    margin: "0 0 6px",
    textTransform: "uppercase",
    fontSize: "12px",
    letterSpacing: "0.08em",
  },

  reflexDesc: {
    fontSize: "15px",
    color: "#6b7280",
    lineHeight: "1.7",
    letterSpacing: "-0.01em",
    margin: "0 0 16px",
  },
};

const MK = () => {
  return (
    <div style={styles.page}>
      <div style={styles.container}>

        {/* Hero */}
        <div style={styles.hero}>
          <img
            src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/Madiha.png"
            alt="Madiha Yawar Khan"
            style={styles.avatar}
          />
          <div style={styles.heroText}>
            <div style={styles.nameRow}>
              <h1 style={styles.name}>Madiha Yawar Khan</h1>
              <FaCheckCircle style={styles.verifiedIcon} />
            </div>
            <p style={styles.title}>Creative Graphic Designer · Pitch Deck Specialist · Virtual Assistant</p>
  
          </div>
        </div>

        {/* About */}
        <section style={styles.section}>
          <div style={styles.badge}>About</div>
          <h2 style={styles.sectionTitle}>Creative Designer & Remote Team Expert</h2>
          <p style={styles.bodyText}>
            I help brands look professional and communicate clearly through design.
            From pitch decks that win investors to social media graphics that stop
            the scroll. I create visuals that work. With 6+ years of experience on
            Upwork and my own Canva template shop on Creative Market, I bring both
            creativity and business sense to every project. Whether you need a
            stunning presentation, a cohesive brand identity, or a reliable virtual
            assistant, I deliver results on time, every time.
          </p>
        </section>

        {/* Find Me On */}
        <section style={styles.section}>
          <div style={styles.badge}>Find Me On</div>
          <div style={styles.findMeGrid}>
            <div style={styles.findMeCard}>
              <img
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/upwork.png"
                alt="Upwork"
                style={styles.findMeLogo}
              />
              <p style={styles.findMeDesc}>Client reviews & work history.</p>
              <a
                href="https://www.upwork.com/freelancers/~01f5173a8f471782cb?mp_source=share"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#6fda44")}
              >
                Upwork
              </a>
            </div>
            <div style={styles.findMeCard}>
              <img
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/linkedin.png"
                alt="LinkedIn"
                style={styles.findMeLogo}
              />
              <p style={styles.findMeDesc}>Career journey & recommendations.</p>
              <a
                href="https://www.linkedin.com/in/madihayawarkhan/"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#0077b5")}
              >
                LinkedIn
              </a>
            </div>
            <div style={styles.findMeCard}>
              <img
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/behance.png"
                alt="Behance"
                style={{ width: "65px", height: "65px", objectFit: "contain" }}
              />
              <p style={styles.findMeDesc}>Portfolio & design samples.</p>
              <a
                href="https://www.behance.net/madihanaz"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#1769ff")}
              >
                Behance
              </a>
            </div>
            <div style={styles.findMeCard}>
              <img
                src="https://pub-b515366b2bdd4fb696b694c901df278b.r2.dev/images/creative%20market.png"
                alt="Creative Market"
                style={styles.findMeLogo}
              />
              <p style={styles.findMeDesc}>Canva templates shop.</p>
              <a
                href="https://creativemarket.com/Visualdesigns_"
                target="_blank"
                rel="noopener noreferrer"
                style={styles.findMeBtn("#a855f7")}
              >
                Templates
              </a>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section style={styles.section}>
          <div style={styles.badge}>6 Years of Results</div>
          <div style={styles.statsGrid}>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>6+</div>
              <div style={styles.statLabel}>Years Experience</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>50+</div>
              <div style={styles.statLabel}>Projects Completed</div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statNumber}>100+</div>
              <div style={styles.statLabel}>Templates Sold</div>
            </div>
          </div>
        </section>

        {/* Skills */}
        <section style={styles.section}>
          <div style={styles.badge}>Skills</div>
          <div style={styles.skillsWrap}>
            {[
              "Canva Design",
              "Pitch Deck Design",
              "Presentation Design",
              "Social Media Graphics",
              "Brand Identity",
              "Logo Design",
              "Infographic Design",
              "Brochure Design",
              "Print Design",
              "Google Slides",
              "Editorial Design",
              "Virtual Assistance",
              "Remote Team Management",
              "Template Design",
              "Content Creation",
            ].map((s) => (
              <span key={s} style={styles.skill}>{s}</span>
            ))}
          </div>
        </section>

        {/* Experience */}
        <section style={styles.section}>
          <div style={styles.badge}>Experience</div>

          {/* Reflex card */}
          <div style={styles.reflexCard}>
            <p style={styles.reflexRole}>Creative Designer & Executive Operations Associate — Reflex</p>
            <p style={styles.reflexDate}>2024 – Present</p>
            <p style={styles.reflexSubHeading}>Design</p>
            <p style={styles.reflexDesc}>
              I own Reflex's visual identity end-to-end designing graphics, building and
              maintaining the website UI, producing polished PDFs, and creating social media
              content that keeps the brand sharp and consistent across every touchpoint.
            </p>
            <p style={styles.reflexSubHeading}>Operations</p>
            <p style={{ ...styles.reflexDesc, marginBottom: 0 }}>
              As a right hand to the CEO, I manage his calendar and meeting schedule, maintain
              and update the CRM, handle the dialer, keep client records accurate and current,
              drive lead generation, and execute the day-to-day tasks that keep the business
              running smoothly.
            </p>
          </div>

          {[
            {
              role: "Freelance Graphic Designer & Pitch Deck Specialist — Upwork",
              date: "2019 – Present",
              desc: "Delivered 50+ design projects for international clients including pitch decks, social media graphics, brand identities, and presentation designs. Maintained strong client satisfaction and repeat business.",
            },
            {
              role: "Template Designer — Creative Market",
              date: "2020 – Present",
              desc: "Created and sold 70+ professional Canva templates used by businesses and entrepreneurs worldwide. Specializing in pitch decks, social media kits, and marketing material.",
            },
            {
              role: "Virtual Assistant & Remote Team Manager",
              date: "2023 – Present",
              desc: "Supported founders and executives with remote team coordination, task management, scheduling, and administrative operations across multiple time zones.",
            },
          ].map((exp) => (
            <div key={exp.role} style={styles.expCard}>
              <p style={styles.expRole}>{exp.role}</p>
              <p style={styles.expDate}>{exp.date}</p>
              <p style={styles.expDesc}>{exp.desc}</p>
            </div>
          ))}
        </section>

        {/* Web Development */}
<section style={styles.section}>
  <div style={styles.badge}>Web Development</div>
  <h2 style={styles.sectionTitle}>Full-Stack Websites & Setup</h2>
  <p style={styles.bodyText}>
    Beyond design, I also deliver complete business websites built on the
    MERN stack — modern, fast, and mobile-friendly. From landing pages to
    multi-page business websites, I handle the full process end to end.
    That includes domain setup, hosting configuration, professional email
    setup, and DNS records — so your website is fully live and ready to
    use from day one, with zero technical headaches on your side.
  </p>
  <div style={styles.skillsWrap}>
    {[
      "MERN Stack Websites",
      "Landing Pages",
      "Business Websites",
      "Domain Setup",
      "Hosting Configuration",
      "Professional Email Setup",
      "DNS Records",
      "Mobile Responsive",
      "SEO Ready",
    ].map((s) => (
      <span key={s} style={styles.skill}>{s}</span>
    ))}
  </div>
</section>

        {/* Portfolio */}
        <section style={{ marginBottom: "48px" }}>
          <div style={styles.badge}>Portfolio</div>
          <h2 style={styles.sectionTitle}>My Design Work</h2>
          <p style={styles.bodyText}>
            Browse my full portfolio of pitch decks, social media graphics, brand
            identities, and Canva templates. Each project is crafted with precision
            and purpose.
          </p>
          <div style={{ display: "flex", gap: "12px", marginTop: "24px", flexWrap: "wrap" }}>
            <a
              href="https://www.behance.net/madihanaz"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "14px 28px",
                background: "#1769ff",
                borderRadius: "12px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: "600",
                textDecoration: "none",
                letterSpacing: "-0.01em",
                boxShadow: "0 4px 16px rgba(23,105,255,0.3)",
              }}
            >
              View Behance Portfolio
            </a>
            <a
              href="https://creativemarket.com/Visualdesigns_"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "14px 28px",
                background: "linear-gradient(135deg, #7c3aed, #a855f7)",
                borderRadius: "12px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: "600",
                textDecoration: "none",
                letterSpacing: "-0.01em",
                boxShadow: "0 4px 16px rgba(124,58,237,0.3)",
              }}
            >
              Browse My Templates
            </a>
          </div>
        </section>

      </div>
    </div>
  );
};

export default MK;